import { useQuery } from '@tanstack/react-query';
import { getMyQuests } from '../../services/questService';

function QuestRow({ quest }) {
  const pct = quest.pct;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl transition-all ${quest.completed ? 'bg-green-50' : 'bg-gray-50'}`}>
      <span className="text-2xl flex-shrink-0">{quest.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-xs font-semibold truncate ${quest.completed ? 'text-green-700 line-through' : 'text-secondary'}`}>
            {quest.title}
          </p>
          <span className={`text-xs font-bold flex-shrink-0 ${quest.completed ? 'text-green-600' : 'text-primary'}`}>
            {quest.completed ? '✓' : `+${quest.xpReward} XP`}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 bg-white rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${quest.completed ? 'bg-green-500' : 'bg-primary'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[10px] text-gray-400 flex-shrink-0">
            {quest.progress}/{quest.criteriaValue}
          </span>
        </div>
      </div>
    </div>
  );
}

function QuestSection({ title, quests, accent }) {
  if (!quests?.length) return null;
  return (
    <div>
      <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${accent}`}>{title}</p>
      <div className="space-y-1.5">
        {quests.map(q => <QuestRow key={q.id} quest={q} />)}
      </div>
    </div>
  );
}

function QuestPanel() {
  const { data, isPending } = useQuery({
    queryKey: ['my-quests'],
    queryFn:  () => getMyQuests().then(r => r.data),
    refetchInterval: 60000,
  });

  const quests = data?.quests;

  const totalCompleted = quests
    ? [...(quests.daily || []), ...(quests.weekly || []), ...(quests.monthly || [])].filter(q => q.completed).length
    : 0;
  const totalQuests = quests
    ? (quests.daily?.length || 0) + (quests.weekly?.length || 0) + (quests.monthly?.length || 0)
    : 0;

  if (isPending) {
    return (
      <div className="space-y-2">
        {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 animate-pulse rounded-xl" />)}
      </div>
    );
  }

  if (!quests) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-secondary">Active Quests</p>
        <span className="text-xs text-gray-400">{totalCompleted}/{totalQuests} done</span>
      </div>

      <QuestSection title="Today"     quests={quests.daily}   accent="text-orange-500" />
      <QuestSection title="This Week" quests={quests.weekly}  accent="text-blue-500"   />
      <QuestSection title="Monthly"   quests={quests.monthly} accent="text-purple-600" />
    </div>
  );
}

export default QuestPanel;
